(function () {
  'use strict';

  class ProductLabelsComponent extends HTMLElement {
    static get observedAttributes() {
      return ['product'];
    }

    constructor() {
      super();
      this._product = null;
    }

    connectedCallback() {
      if (!this._product && this.hasAttribute('product')) {
        this._product = this.parseProduct(this.getAttribute('product'));
      }

      this.render();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'product' || oldValue === newValue) return;

      this._product = this.parseProduct(newValue);
      this.render();
    }

    get product() {
      return this._product;
    }

    set product(value) {
      this._product = value;
      this.render();
    }

    parseProduct(value) {
      if (!value) return null;
      if (typeof value === 'object') return value;

      try {
        return JSON.parse(value);
      } catch (error) {
        return null;
      }
    }

    isPresent(value) {
      if (value == null) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'object' && 'value' in value) return this.isPresent(value.value);
      return true;
    }

    getMetafieldValue(product, namespace, key) {
      const metafields = product?.metafields;

      if (!metafields) return null;

      if (!Array.isArray(metafields)) {
        return metafields?.[namespace]?.[key] ?? null;
      }

      if (namespace === 'display' && key === 'mindful_brand') {
        return metafields[0] ?? null;
      }

      if (namespace === 'custom' && key === 'bonus_message') {
        return metafields[1] ?? null;
      }

      return null;
    }

    getPriceNumber(value) {
      if (value == null || value === '') return 0;

      const amount = typeof value === 'object' && 'amount' in value ? value.amount : value;
      const parsed = Number.parseFloat(amount);

      return Number.isNaN(parsed) ? 0 : parsed;
    }

    getActiveVariant(product) {
      return product?.selected_variant || product?.selectedVariant || product?.variants?.[0] || null;
    }

    getProductTags(product) {
      const tags = product?.tags;

      if (Array.isArray(tags)) {
        return tags.filter(Boolean);
      }

      if (typeof tags === 'string') {
        return tags
          .split(',')
          .map(function (tag) {
            return tag.trim();
          })
          .filter(Boolean);
      }

      return [];
    }

    getPriceState(item) {
      const price = this.getPriceNumber(item?.price);
      const compareAtPrice = this.getPriceNumber(
        item?.compare_at_price ?? item?.compareAtPrice
      );

      return { price, compareAtPrice };
    }

    shouldShowMindfulBrand(product) {
      return Boolean(window.mindfulBrandIconUrl) && this.isPresent(this.getMetafieldValue(product, 'display', 'mindful_brand'));
    }

    shouldShowBonusBadge(product) {
      return this.isPresent(this.getMetafieldValue(product, 'custom', 'bonus_message'));
    }

    shouldShowSaleBadge(product) {
      const settings = window.saleBadgeSettings || {};
      if (!settings.enabled) return false;

      const variants = Array.isArray(product?.variants) && product.variants.length
        ? product.variants
        : [this.getActiveVariant(product) || product];
      return variants.some((variant) => {
        const { price, compareAtPrice } = this.getPriceState(variant);
        return compareAtPrice > price;
      });
    }

    getTagBadges(product) {
      const badgeMap = window.rccBadgeMap || {};
      const saleBadgeMap = window.rccSaleBadgeMap || {};
      const tags = this.getProductTags(product);
      const badges = [];

      for (const tag of tags) {
        const badge = badgeMap[tag];
        if (!badge || saleBadgeMap[tag]) continue;

        badges.push({
          text: badge.text,
          bg: badge.bg,
          color: badge.color
        });
      }

      return badges;
    }

    getSaleBadgeOverride(product) {
      const saleBadgeMap = window.rccSaleBadgeMap || {};
      const tags = this.getProductTags(product);

      for (const tag of tags) {
        if (saleBadgeMap[tag]) return saleBadgeMap[tag];
      }

      return null;
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    renderBadge(text, className, styleVars) {
      const style = styleVars ? ` style="${styleVars}"` : '';
      return `<div class="product-badges__badge product-badges__badge--rectangle ${className}"${style}>${this.escapeHtml(text)}</div>`;
    }

    render() {
      const product = this.product;
      const labels = [];
      const saleSettings = window.saleBadgeSettings || {};
      const saleBadgeOverride = product ? this.getSaleBadgeOverride(product) : null;

      if (product && this.shouldShowSaleBadge(product)) {
        labels.push(
          this.renderBadge(
            saleBadgeOverride?.text || saleSettings.text || 'Sale',
            'product-badges__badge--sale',
            [
              '--product-label-bg: ' + (saleBadgeOverride?.bg || saleSettings.bgColor || '#c00'),
              '--product-label-color: ' + (saleBadgeOverride?.color || saleSettings.textColor || '#fff')
            ].join('; ')
          )
        );
      }

      if (product && this.shouldShowBonusBadge(product)) {
        labels.push(this.renderBadge('BONUS', 'product-badges__badge--bonus'));
      }

      if (product) {
        for (const badge of this.getTagBadges(product)) {
          labels.push(
            this.renderBadge(
              badge.text,
              'product-badges__badge--tag-based',
              [
                '--product-label-bg: ' + badge.bg,
                '--product-label-color: ' + badge.color
              ].join('; ')
            )
          );
        }
      }

      const logoMarkup = product && this.shouldShowMindfulBrand(product)
        ? `
          <div class="product-badges__logo">
            <img
              src="${window.mindfulBrandIconUrl}"
              alt="Mindful Brand"
              width="50"
              height="50"
              loading="lazy"
            >
          </div>
        `
        : '';

      this.innerHTML = `
        <div class="product-badges-row">
          <div class="product-badges product-badges--rcc">
            ${labels.join('')}
          </div>
          ${logoMarkup}
        </div>
      `;
    }
  }

  if (!customElements.get('rcc-product-labels')) {
    customElements.define('rcc-product-labels', ProductLabelsComponent);
  }
})();
